import {
  LABS_FEATURE,
  useLabsFeatureOptIn,
  useLabsOverrides,
} from '@/features/settings/heidi-labs/early-access/labs-overrides';
import { useSetLabsFeatureOptIn } from '@/features/settings/heidi-labs/early-access/use-set-labs-feature-opt-in';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { FlaskConical } from 'lucide-react';
import { useCallback } from 'react';

interface LabsSessionLayoutV2DevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools shortcut for the Heidi Labs "Session Layout V2" opt-in
 * ({@link LABS_FEATURE.sessionLayoutV2}). The tri-state `optIn ?? flag` drives
 * behaviour: `true` -> V2 layout, `false` -> V1 (opted out), `undefined` -> V1
 * AND eligible for the opt-in invite card.
 *
 * `setOptIn` can only persist `true`/`false`, so "Clear" writes the override map
 * without the key directly via `useMutateUpdateUser` to return to `undefined` —
 * which re-enables the opt-in invite card for testing.
 */
export function LabsSessionLayoutV2DevSection({
  onRequestClose,
}: Readonly<LabsSessionLayoutV2DevSectionProps>) {
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const current = useLabsOverrides();
  const { setOptIn } = useSetLabsFeatureOptIn();
  const { mutate: updateUser } = useMutateUpdateUser();

  const state =
    optIn === true ? 'Opted in (V2)' : optIn === false ? 'Opted out (V1)' : 'Not set';

  const handleOptIn = useCallback(() => {
    setOptIn(LABS_FEATURE.sessionLayoutV2, true);
    onRequestClose?.();
  }, [setOptIn, onRequestClose]);

  const handleOptOut = useCallback(() => {
    setOptIn(LABS_FEATURE.sessionLayoutV2, false);
    onRequestClose?.();
  }, [setOptIn, onRequestClose]);

  const handleClear = useCallback(() => {
    // Clear re-enables the opt-in invite card by returning the value to `undefined`.
    const mapWithoutKey = { ...current };
    delete mapWithoutKey[LABS_FEATURE.sessionLayoutV2];
    updateUser({ session_settings: { labs_feature_overrides: mapWithoutKey } });
    onRequestClose?.();
  }, [current, updateUser, onRequestClose]);

  return (
    <section aria-labelledby="dev-tools-labs-session-layout-v2-heading" className="mb-6">
      <TypographyP2SemiBold
        id="dev-tools-labs-session-layout-v2-heading"
        className="mb-2"
      >
        Session Layout V2 (Labs)
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Toggles the per-user{' '}
        <span className="font-mono">{LABS_FEATURE.sessionLayoutV2}</span> Labs opt-in.
        Current: <span className="font-semibold">{state}</span>. Clear re-surfaces the
        opt-in invite card.
      </TypographyCaption>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleOptIn}
          data-testid="dev-tools-labs-session-layout-v2-opt-in"
        >
          <FlaskConical size={14} aria-hidden /> Opt in (V2)
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleOptOut}
          data-testid="dev-tools-labs-session-layout-v2-opt-out"
        >
          <FlaskConical size={14} aria-hidden /> Opt out (V1)
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleClear}
          data-testid="dev-tools-labs-session-layout-v2-clear"
        >
          <FlaskConical size={14} aria-hidden /> Clear (show invite)
        </Button>
      </div>
    </section>
  );
}
